import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const MortgageCalculatorLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Instant Calculations',
      description: 'Calculate monthly payments in seconds with our advanced calculator',
      icon: '⚡',
    },
    {
      title: 'Multiple Scenarios',
      description: 'Compare different loan amounts, rates, and terms',
      icon: '📊',
    },
    {
      title: 'Irish Mortgage Rates',
      description: 'Pre-populated with current Irish mortgage rates',
      icon: '🏦',
    },
    {
      title: 'Detailed Breakdown',
      description: 'See principal, interest, and total costs clearly',
      icon: '📋',
    },
    {
      title: 'Export Reports',
      description: 'Download and share detailed amortization schedules',
      icon: '📥',
    },
    {
      title: 'Tax Considerations',
      description: 'Factor in mortgage relief and Irish tax implications',
      icon: '💰',
    },
  ];

  const benefits = [
    'Make informed financial decisions quickly',
    'Understand total cost of borrowing',
    'Compare multiple scenarios easily',
    'Plan your budget accurately',
    'Share reports with financial advisors',
  ];

  return (
    <>
      <Helmet>
        <title>Mortgage Calculator Ireland | Calculate Monthly Payments | RentSmart.ie</title>
        <meta
          name="description"
          content="Use RentSmart's free Mortgage Calculator to estimate your monthly payments. Perfect for first-time buyers and investors in Ireland. Instant calculations with Irish mortgage rates."
        />
        <meta
          name="keywords"
          content="mortgage calculator, Ireland, monthly payments, loan calculator, property calculator, first-time buyers, mortgage rates, Irish mortgage"
        />
        <link
          rel="canonical"
          href="https://rentsmart.ie/property-tools/mortgage-calculator-landing"
        />
        <meta property="og:title" content="Mortgage Calculator - RentSmart.ie" />
        <meta
          property="og:description"
          content="Calculate your monthly mortgage payments with RentSmart's free Mortgage Calculator for Irish properties."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/mortgage-calculator-landing"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Know Your Mortgage
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600">
                Payments Instantly
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Calculate monthly payments, compare scenarios, and understand your total borrowing
              costs. Free calculator for Irish borrowers.
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Use Mortgage Calculator
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Everything You Need to Know
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Use Our Mortgage Calculator?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Enter Loan Details',
                  description: 'Input property price, down payment, and loan term',
                },
                {
                  step: 2,
                  title: 'Set Interest Rate',
                  description: 'Choose your interest rate from current Irish rates',
                },
                {
                  step: 3,
                  title: 'View Results',
                  description: 'See monthly payment, total interest, and full breakdown',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-50 to-teal-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Perfect For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'First-Time Buyers',
                  description: 'Understand affordability before applying for a mortgage',
                },
                {
                  title: 'Investors',
                  description: 'Calculate ROI and cash flow for investment properties',
                },
                {
                  title: 'Refinancers',
                  description: 'Compare current rates to make refinancing decisions',
                },
              ].map((useCase, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-gray-600">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-600 to-teal-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Calculate Your Mortgage?
            </h2>
            <p className="text-lg mb-8 text-green-100">
              Get instant insights into your monthly payments and total borrowing costs
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Access Mortgage Calculator
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default MortgageCalculatorLandingPage;
